import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Users, Bell, Calendar, UserCheck, ChevronRight, Contact } from 'lucide-react';

export function ResidentTiles({
  visitors,
  notices,
  myStaff,
  activeStaffOnCampus,
  facilities,
  bookingNotifications,
  mcMembersCount,
  neighborsCount,
  setShowVisitorsModal,
  setShowNoticesModal,
  setShowStaffModal,
  setShowFacilitiesModal,
  setShowMCModal,
  setShowNeighborsModal,
  config
}) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <Card
        data-testid="visitors-tile"
        className="shadow border-0 cursor-pointer hover:shadow-lg transition-shadow group"
        onClick={() => setShowVisitorsModal(true)}
      >
        <CardContent className="p-3 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <Users className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm sm:text-base font-medium text-slate-600 leading-tight">Recent Visitors</p>
                <p className="text-3xl sm:text-4xl font-extrabold text-blue-600">{visitors.length}</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block group-hover:text-blue-500 transition-colors" />
          </div>
        </CardContent>
      </Card>

      <Card
        data-testid="notices-tile"
        className="shadow border-0 cursor-pointer hover:shadow-lg transition-shadow group"
        onClick={() => setShowNoticesModal(true)}
      >
        <CardContent className="p-3 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                <Bell className="w-4 h-4 sm:w-6 sm:h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm sm:text-base font-medium text-slate-600 leading-tight">Management Posts</p>
                <p className="text-3xl sm:text-4xl font-extrabold text-amber-600">{notices.length}</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block group-hover:text-amber-500 transition-colors" />
          </div>
        </CardContent>
      </Card>

      <Card
        data-testid="staff-tile"
        className="shadow border-0 cursor-pointer hover:shadow-lg transition-shadow group"
        onClick={() => setShowStaffModal(true)}
      >
        <CardContent className="p-3 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-gk-bg flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                <UserCheck className="w-4 h-4 sm:w-6 sm:h-6 text-gk-teal" />
              </div>
              <div>
                <p className="text-sm sm:text-base font-medium text-slate-600 leading-tight">My Staff/Maids</p>
                <p className="text-3xl sm:text-4xl font-extrabold text-gk-teal">{myStaff.length}</p>
                {activeStaffOnCampus.length > 0 && (
                  <p className="text-xs text-emerald-500">{activeStaffOnCampus.length} on campus</p>
                )}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block group-hover:text-gk-teal transition-colors" />
          </div>
        </CardContent>
      </Card>

      {config?.facility_booking_enabled !== false && (
        <Card
          data-testid="facilities-tile"
          className="shadow border-0 cursor-pointer hover:shadow-lg transition-shadow group"
          onClick={() => setShowFacilitiesModal(true)}
        >
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-gk-bg flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                  <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-gk-navy" />
                </div>
                <div>
                  <p className="text-sm sm:text-base font-medium text-slate-600 leading-tight">Book Facilities</p>
                  <p className="text-3xl sm:text-4xl font-extrabold text-gk-navy">{facilities.length}</p>
                  {bookingNotifications.filter(n => !n.read).length > 0 && (
                    <p className="text-xs text-gk-teal">{bookingNotifications.filter(n => !n.read).length} new update{bookingNotifications.filter(n => !n.read).length > 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block group-hover:text-gk-navy transition-colors" />
            </div>
          </CardContent>
        </Card>
      )}

      {config?.mc_list_enabled && (
        <Card
          data-testid="mc-tile"
          className="shadow border-0 cursor-pointer hover:shadow-lg transition-shadow group"
          onClick={() => setShowMCModal && setShowMCModal(true)}
        >
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-pink-100 flex items-center justify-center group-hover:bg-pink-200 transition-colors">
                  <Users className="w-4 h-4 sm:w-6 sm:h-6 text-pink-600" />
                </div>
                <div>
                  <p className="text-sm sm:text-base font-medium text-slate-600 leading-tight">Management Committee</p>
                  <p className="text-3xl sm:text-4xl font-extrabold text-pink-600">{mcMembersCount ?? 0}</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block group-hover:text-pink-500 transition-colors" />
            </div>
          </CardContent>
        </Card>
      )}

      {config?.neighbors_directory_enabled && (
        <Card
          data-testid="neighbors-tile"
          className="shadow border-0 cursor-pointer hover:shadow-lg transition-shadow group"
          onClick={() => setShowNeighborsModal && setShowNeighborsModal(true)}
        >
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                  <Contact className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm sm:text-base font-medium text-slate-600 leading-tight">Neighbors</p>
                  <p className="text-3xl sm:text-4xl font-extrabold text-indigo-600">{neighborsCount ?? 0}</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block group-hover:text-indigo-500 transition-colors" />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
